import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import {
  AutoFixHigh as AutoFixHighIcon,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';

interface EnrichmentSummaryBannerProps {
  /** Schema metadata blob (`draft.metadata`). The banner reads
   *  enrichment-related fields stamped by the backend at the end of an
   *  enrichment run. */
  metadata: Record<string, any>;
  /** Re-run the descriptions phase of the annotation pipeline. */
  onRetry?: () => void;
  /** Disable the retry button (e.g., template still needs annotation). */
  disabled?: boolean;
}

/**
 * One-line plain-language summary of the last enrichment run.
 *
 *   "Last enriched 2 days ago · 11 tables described · 0 failed"
 *
 * Click to expand for token usage and the list of failed tables.
 * Renders nothing when no enrichment has run.
 */
const EnrichmentSummaryBanner: React.FC<EnrichmentSummaryBannerProps> = ({ metadata, onRetry, disabled }) => {
  const [expanded, setExpanded] = useState(false);

  const enrichedFlag = metadata?.enriched_by_llm;
  if (!enrichedFlag) return null;

  const timestamp: string | undefined = metadata?.enrichment_timestamp;
  const totalChunks: number = Number(metadata?.enrichment_chunks_total ?? 0);
  const succeededChunks: number = Number(metadata?.enrichment_chunks_succeeded ?? 0);
  const failedChunks: number = Number(metadata?.enrichment_chunks_failed ?? 0);
  const failedTables: string[] = Array.isArray(metadata?.enrichment_tables_failed)
    ? metadata.enrichment_tables_failed
    : [];
  const sampleUsed: boolean = !!metadata?.enrichment_sample_data_used;
  const tokens: { input_tokens?: number; output_tokens?: number } | undefined =
    metadata?.enrichment_token_usage;

  const friendlyTimestamp = formatRelativeTime(timestamp);
  const hasFailures = failedChunks > 0 || failedTables.length > 0;
  const severity: 'info' | 'warning' = hasFailures ? 'warning' : 'info';

  return (
    <Alert
      severity={severity}
      variant="outlined"
      icon={<AutoFixHighIcon fontSize="small" />}
      sx={{ mb: 2, '& .MuiAlert-message': { width: '100%' } }}
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          {onRetry && hasFailures && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={onRetry}
              disabled={disabled}
            >
              Retry enrichment
            </Button>
          )}
          <Button
            size="small"
            onClick={() => setExpanded((v) => !v)}
            endIcon={expanded ? <ExpandLess /> : <ExpandMore />}
          >
            Details
          </Button>
        </Stack>
      }
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        <Typography variant="body2" component="span">
          {friendlyTimestamp ? `Last enriched ${friendlyTimestamp}` : 'Schema enriched'}
        </Typography>
        {totalChunks > 0 && (
          <Typography variant="body2" component="span" color="text.secondary">
            · {succeededChunks} of {totalChunks} table{totalChunks !== 1 ? 's' : ''} described
          </Typography>
        )}
        {hasFailures && (
          <Chip
            label={`${failedChunks || failedTables.length} failed`}
            size="small"
            color="warning"
            variant="outlined"
          />
        )}
      </Stack>

      <Collapse in={expanded}>
        <Box sx={{ mt: 1 }}>
          {timestamp && (
            <Typography variant="caption" color="text.secondary" display="block">
              Run finished at {new Date(timestamp).toLocaleString()}
            </Typography>
          )}
          {sampleUsed && (
            <Typography variant="caption" color="text.secondary" display="block">
              Used sample data from your files to ground descriptions.
            </Typography>
          )}
          {tokens && (typeof tokens.input_tokens === 'number' || typeof tokens.output_tokens === 'number') && (
            <Typography variant="caption" color="text.secondary" display="block">
              Tokens used:{' '}
              {(tokens.input_tokens ?? 0).toLocaleString()} in ·{' '}
              {(tokens.output_tokens ?? 0).toLocaleString()} out
            </Typography>
          )}
          {failedTables.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" fontWeight={600} color="text.secondary">
                Tables that failed to enrich:
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5, gap: 0.5 }}>
                {failedTables.map((t) => (
                  <Chip key={t} label={t} size="small" color="warning" variant="outlined" />
                ))}
              </Stack>
            </Box>
          )}
        </Box>
      </Collapse>
    </Alert>
  );
};

/** "2 days ago" / "just now" / "5 minutes ago" — keep terse, no jargon. */
function formatRelativeTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 0) return 'just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min !== 1 ? 's' : ''} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr !== 1 ? 's' : ''} ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day} day${day !== 1 ? 's' : ''} ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo} month${mo !== 1 ? 's' : ''} ago`;
  const yr = Math.floor(mo / 12);
  return `${yr} year${yr !== 1 ? 's' : ''} ago`;
}

export default EnrichmentSummaryBanner;
